/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 2 hook(s) need dependency review; fix each and remove this directive */
import { useTranslation } from "@i18n";
import { forwardRef, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { getTakeoffsList, isEstimateRequiredPermission } from "@Actions/estimation";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import CreateNewTakeoffModal from "../CreateNewTakeoffModal";
import { folderPermissions } from "@Helpers/constants";
import { useSelector } from "react-redux";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import IconComponent from "@CubeComponents/icons";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Text from "@CubeComponents/html/Text/Text";
import { RootState } from "@Reducers/index";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";
import type { GetTakeoffsListResponse } from "@ServerTypes/takeoffs/takeoffs.schema";

/** One takeoff as `POST /takeoffs/getTakeoffsList` returns it. */
type TakeoffListItem = NonNullable<GetTakeoffsListResponse["result"]>[number];

const { RESOURCE_MODIFY } = folderPermissions();

interface AddToTakeoffButtonProps {
  /**
   * Parent estimate id. Reaches this button through the viewer's untyped (`any`) prop chain from
   * the `estimateId` URL query param; a legacy numeric form is still coerced with `String()`
   * before it is sent, hence the union.
   */
  estimateId: ObjectIdString | number;
  /** Measurement ids to exclude from the list; forwarded as `excludeMeasurements`. */
  itemsMarked?: string[] | null;
  onSuccess?: (ids: ObjectIdString[]) => void | Promise<void>;
  onCreated?: () => void;
}

const AddToTakeoffButton = forwardRef<HTMLButtonElement, AddToTakeoffButtonProps>(
  ({ estimateId, itemsMarked, onSuccess, onCreated }, ref) => {
    const { t } = useTranslation();
    const location = useLocation();
    const [takeoffsList, setTakeoffsList] = useState<TakeoffListItem[]>([]);
    const [showTakeoffsList, setShowTakeoffsList] = useState<boolean>(false);
    const [takeoffsChecked, setTakeoffsChecked] = useState<TakeoffListItem[]>([]);
    const [showNewTakeoffModal, setShowNewTakeoffModal] = useState<boolean>(false);
    const [isAssigning, setIsAssigning] = useState<boolean>(false);
    const innerRef = useRef<HTMLButtonElement | null>(null);
    const addToButtonRef = ref || innerRef;

    const estimateData = useSelector((state: RootState) => state.estimateData);
    const [searchable, setSearchable] = useState<boolean>(false);

    const urlSearchParams = new URLSearchParams(location.search);
    // External entry points: the estimate id arrives via the viewer's untyped prop chain and the
    // version id as a URL query param. Both are adopted once here, at their declaration (the
    // pre-existing String() coercion of the legacy numeric estimate id is preserved).
    const estimateObjectId = String(estimateId) as ObjectIdString;
    const estimateVersionId = urlSearchParams.get("versionId") as ObjectIdString | null;

    const addButtonClickHandler = () => {
      setShowTakeoffsList(!showTakeoffsList);
    };

    useEffect(() => {
      if (onCreated) {
        onCreated();
      }
    }, []);

    useEffect(() => {
      if (showTakeoffsList) {
        setTakeoffsChecked([]);
        fetchTakeoffsList();
      }
    }, [showTakeoffsList]);

    const fetchTakeoffsList = async () => {
      const response = await getTakeoffsList({
        estimateId: estimateObjectId,
        excludeMeasurements: itemsMarked,
        versionId: estimateVersionId ?? undefined,
      });
      if (response.data.status) {
        setTakeoffsList(response.data.result ?? []);
        if (response.data.result?.length) {
          setSearchable(true);
        }
      }
    };

    const attachMeasurementsToTakeoffs = async () => {
      if (!onSuccess) {
        setShowTakeoffsList(false);
        return;
      }
      setIsAssigning(true);
      await onSuccess(takeoffsChecked.map((takeoff) => takeoff.takeOffItemId));
      setIsAssigning(false);
      setShowTakeoffsList(false);
    };
    const startNewTakeoff = () => {
      setShowNewTakeoffModal(true);
      setTakeoffsChecked([]);
    };

    // `multiple` is set, so the dropdown always hands back the selected array; its prop type also
    // covers the single-select and cleared cases. Erased assertion at the one read.
    const onChange = (options: TakeoffListItem | TakeoffListItem[] | null) => {
      setTakeoffsChecked(options as TakeoffListItem[]);
    };

    const closeTakeoffModal = () => setShowNewTakeoffModal(false);
    const displayTemplate = () => {
      const disabled = estimateVersionId || !isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id);
      return (
        <ButtonComponent
          onClick={addButtonClickHandler}
          ref={addToButtonRef}
          disabled={!!disabled}
          sx={{ flexShrink: 0 }}
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          startIcon={
            <div className={`flex items-center  ${disabled ? "opacity-50" : ""} justify-center w-6 h-6`}>
              <IconComponent name={"AssigningToTakeOffs"} width="1.5rem" height={"1.5rem"} />
            </div>
          }
          endIcon={
            <span className={disabled ? "opacity-50" : ""}>
              <ArrowDropDownIcon />
            </span>
          }
        >
          <Text>{t("Assign to Takeoffs")}</Text>
        </ButtonComponent>
      );
    };
    const headerTemplate = () => {
      return (
        <div className="flex px-5 py-2 justify-between items-center justify-items-center w-full">
          <span className="text-[color:var(--Primary-Dark,#192048)] text-base font-bold leading-normal">
            <Text>{t("Takeoffs")}</Text>
          </span>
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} onClick={startNewTakeoff}>
            <div className="flex gap-1 items-center">
              <IconComponent name="add" width="1.5rem" height={"1.5rem"} />
              <Text>{t("New")}</Text>
            </div>
          </ButtonComponent>
        </div>
      );
    };

    const footerTemplate = () => {
      return takeoffsList.length ? (
        <ButtonComponent
          buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
          onClick={attachMeasurementsToTakeoffs}
          disabled={!takeoffsChecked.length || isAssigning}
          loading={isAssigning}
          sx={{ marginInlineStart: "1.25rem" }}
        >
          <Text>{t("Assign")}</Text>
        </ButtonComponent>
      ) : (
        <div className="flex w-full bg-white items-center justify-center flex-col">
          <img alt="noData" src={`${process.env.REACT_APP_IMAGES_PUBLIC_URL}/NoData.svg`} />
          <span className="text-[color:var(--Text-text-primary,#192048)] mt-4 text-sm font-semibold leading-[120%]">
            <Text>{t("No takeoffs available")}</Text>
          </span>
        </div>
      );
    };

    const createTakeoffCallback = () => {
      fetchTakeoffsList();
    };

    return (
      <>
        <div>
          <SelectDropDown
            options={takeoffsList}
            fields={{ text: "name", value: "id" }}
            searchable={searchable}
            multiple
            showNoOptions={false}
            value={takeoffsChecked}
            headerTemplate={headerTemplate}
            footerTemplate={footerTemplate}
            displayTemplate={displayTemplate}
            popperWidth={350}
            menuSx={{ zIndex: 1200 }}
            maxHeight={450}
            onChange={onChange}
            open={showTakeoffsList}
            onClose={() => setShowTakeoffsList(false)}
            disableClickAwayListener={showNewTakeoffModal || isAssigning}
          />
        </div>
        {showNewTakeoffModal && (
          <CreateNewTakeoffModal
            // The legacy numeric form only reaches this button through the viewer's untyped prop
            // chain; the modal (and the create request) want the 24-hex id.
            estimateId={estimateId as ObjectIdString}
            closeTakeoffModal={closeTakeoffModal}
            createCallback={createTakeoffCallback}
          />
        )}
      </>
    );
  },
);

AddToTakeoffButton.displayName = "AddToTakeoffButton";
export default AddToTakeoffButton;
